import {MatchingPdf} from '@/hooks/panier/usePanierPdf';
import {State} from '@synako/halitrek';
import {EtatMatching, LigneMatching, LigneProblem} from 'api-types/ffconnect';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {LigneAvecProbleme} from './PanierContent';

/**
 * Import PDF : les règles de la liste du panier PDF — ce qu'elle contient, dans
 * quel ordre, l'état d'une ligne et ce que chaque filtre retient. Hors de tout
 * composant, pour que `PanierContent` se contente de les appeler et reste
 * lisible pour le panier ordinaire, qu'il rend toujours de la même façon.
 */

export type RaisonOrpheline =
  | 'aucune_correspondance'
  | 'article_indisponible'
  | 'supprimee';

export type ElementPanierPdf =
  | {
      // Ligne réellement présente dans le panier.
      type: 'ligne';
      ligneDePanier: State<LigneDePanierDesc>;
      probleme: LigneProblem | undefined;
      matching: LigneMatching | undefined;
    }
  | {
      // Ligne du PDF sans ligne de panier à afficher : existe que dans le rapport du processus mais pas dans le panier,
      // et doit quand même apparaître dans la liste.
      // - aucune_correspondance : le matching n'a identifié aucun article
      // - article_indisponible : un article a été identifié, mais il n'a pas pu
      //   être ajouté au panier (plus au catalogue du client à la transformation)
      // - supprimee : la ligne a été créée dans le panier, puis retirée depuis
      type: 'orphelin';
      ligneMatching: LigneMatching;
      raison: RaisonOrpheline;
    };

// La liste affichée d'un panier PDF : l'union des lignes de panier et des lignes
// du bon de commande restées sans ligne de panier, dans l'ordre du PDF.
export const composerElementsPanierPdf = (
  lignesAvecProbleme: LigneAvecProbleme[], // toutes les lignes du panier, chacune avec son problème éventuel
  matchingPdf: MatchingPdf,
): ElementPanierPdf[] => {
  // Ids de toutes les lignes affichées (`lignesAvecProbleme` = les lignes du
  // panier, accompagnées de leur problème éventuel). Sert plus bas à repérer
  // les lignes du PDF dont la ligne de panier a disparu.
  const idslignesDePanier = new Set(
    lignesAvecProbleme.map((ligne) => ligne.ligneDePanier.data.id),
  );

  // Chaque ligne du panier, étiquetée et rattachée à la ligne du PDF dont elle
  // provient lorsqu'elle en a une.
  const lignes: ElementPanierPdf[] = lignesAvecProbleme.map(
    ({ligneDePanier, probleme}) => {
      return {
        type: 'ligne',
        ligneDePanier,
        probleme,
        matching: matchingPdf.parLignePanier.get(ligneDePanier.data.id),
      };
    },
  );

  const lignesOrphelines: ElementPanierPdf[] = [];
  // - aucune ligne n'a jamais été créée. Reste à savoir pourquoi : le serveur
  //   saute aussi bien les lignes sans article identifié que celles dont
  //   l'article n'était plus au catalogue au moment de la transformation, et
  //   n'écrit de correspondance ni dans un cas ni dans l'autre. C'est la
  //   présence de `tolede` qui les distingue.
  for (const ligneMatching of matchingPdf.sansLignePanier) {
    lignesOrphelines.push({
      type: 'orphelin',
      ligneMatching,
      raison: ligneMatching.item_matche.tolede
        ? 'article_indisponible'
        : 'aucune_correspondance',
    });
  }
  // - la ligne a existé, mais elle a été retirée du panier
  for (const [lignePanierId, ligneMatching] of matchingPdf.parLignePanier) {
    if (!idslignesDePanier.has(lignePanierId)) {
      lignesOrphelines.push({
        type: 'orphelin',
        ligneMatching,
        raison: 'supprimee',
      });
    }
  }

  // Ordre du bon de commande : remet les lignes dans l'ordre du PDF
  // Une ligne du PDF restée sans ligne de panier garde sa place.
  // Les lignes ajoutées après l'import, sont ajoutées à la fin dans leur ordre d'arrivée.
  return [...lignes, ...lignesOrphelines].sort((a, b) => {
    const rangA = numeroLignePdf(a);
    const rangB = numeroLignePdf(b);
    // Comparaison et non soustraction : deux Infinity donneraient NaN.
    return rangA === rangB ? 0 : rangA < rangB ? -1 : 1;
  });
};

// Rang de l'élément dans le bon de commande. Une ligne ajoutée après l'import
// (catalogue, article différé, panier commercial fusionné) n'en a pas et finira à la fin.
const numeroLignePdf = (element: ElementPanierPdf): number => {
  return element.type === 'orphelin'
    ? element.ligneMatching.donnees_extraites.numero_ligne_pdf
    : (element.matching?.donnees_extraites.numero_ligne_pdf ?? Infinity);
};

// Récupération de l'etat de la ligne commande.
// Si absent => "bloquant" comme le compte le serveur (calculerStatistiquesLignes)
export const etatDeLaLigne = (ligneMatching: LigneMatching): EtatMatching => {
  return ligneMatching.matching.etat ?? 'bloquant';
};

// Filtre actif dans l'en-tête du panier PDF, piloté par les chips de statut.
export type FiltreMatching = 'tout' | 'ok' | 'alerte' | 'bloquant';

// Correspondance entre le filtre demandé et les elements PDF a afficher.
export const correspondAuFiltre = (
  element: ElementPanierPdf,
  filtre: FiltreMatching,
): boolean => {
  // Filtre qui récupère toutes les lignes du panier sans distinction
  if (filtre === 'tout') return true;

  // L'état vit à un endroit différent selon la nature de l'élément (ligne ou orphelin)
  const ligneMatching =
    element.type === 'orphelin' ? element.ligneMatching : element.matching;

  // Ligne ajoutée après l'import : elle n'est au bon de commande sous aucun statut
  if (!ligneMatching) return false;

  return etatDeLaLigne(ligneMatching) === filtre;
};
